'use client'

import { CHAIN_CONFIG, extractRelayOpsForChain, getRelayQuote } from '@/lib/api/relay'
import {
	ACTIVE_BRIDGE_STALE_MS,
	type ActiveBridgeState,
	clearActiveBridge,
	isWatcherLockOwned,
	readActiveBridge,
	refreshWatcherLock,
	releaseWatcherLock,
	tryClaimWatcherLock,
} from '@/lib/hyperliquid/active-bridge'
import { hlLog } from '@/lib/hyperliquid/debug'
import { arbitrumReadClient } from '@/lib/onchain/arbitrum-read-client'
import { sendSponsoredCalls } from '@/lib/wallet-core/sponsored'
import { useWallet } from '@/providers/WalletContext'
import { captureApiError } from '@/utils/sentry-helpers'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { type Address, erc20Abi } from 'viem'

const ARB_USDC = CHAIN_CONFIG.arbitrum.usdc as Address
const TICK_INTERVAL_MS = 8_000
const ARB_POLL_INTERVAL_MS = 10_000
const ARB_POLL_DEADLINE_MS = 6 * 60 * 1000
const RELAY_QUOTE_RETRIES = 3
const RELAY_QUOTE_RETRY_BACKOFF_MS = 2_000

function makeTabId(): string {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

async function fetchRelayQuoteWithRetry(params: Parameters<typeof getRelayQuote>[0]) {
	let lastErr: unknown
	for (let attempt = 0; attempt < RELAY_QUOTE_RETRIES; attempt++) {
		try {
			return await getRelayQuote(params)
		} catch (e) {
			lastErr = e
			if (attempt < RELAY_QUOTE_RETRIES - 1) {
				await new Promise(r => setTimeout(r, RELAY_QUOTE_RETRY_BACKOFF_MS * 2 ** attempt))
			}
		}
	}
	throw lastErr
}

async function readArbBalance(eoa: Address): Promise<bigint> {
	return (await arbitrumReadClient.readContract({
		address: ARB_USDC,
		abi: erc20Abi,
		functionName: 'balanceOf',
		args: [eoa],
	})) as bigint
}

/**
 * Background watcher for HL → Polygon/Base/BSC bridges. `useInternalTransfer`
 * persists an ActiveBridgeState to localStorage right after withdraw3 and
 * returns immediately. This hook, mounted at the app root, picks up the
 * state and finishes the flow (Arbitrum credit poll → fresh Relay quote →
 * sponsored userOp) regardless of whether the modal is open or the page
 * got refreshed.
 *
 * Cross-tab safety: localStorage is shared, so two tabs of the same user
 * would otherwise both fire the Relay leg. `tryClaimWatcherLock` gates
 * the work behind a 30s-TTL lock that the running tab refreshes during
 * its poll. Stale entries (> 30min) are cleared and the stranded-recovery
 * banner takes over.
 */
export function useHyperliquidBridgeWatcher() {
	const t = useTranslations('hyperliquid.toast')
	const { eoaAddress, evmAccount } = useWallet()
	const inFlightRef = useRef(false)
	const accountRef = useRef(evmAccount)
	accountRef.current = evmAccount

	const accountReady = evmAccount != null
	const eoaForEffect = eoaAddress ?? null

	useEffect(() => {
		if (!eoaForEffect || !accountReady) return
		const eoa = eoaForEffect as Address
		const tabId = makeTabId()
		let cancelled = false
		inFlightRef.current = false

		async function runBridge(state: ActiveBridgeState): Promise<void> {
			const baseline = BigInt(state.arbBaselineRaw)
			const expected = BigInt(state.expectedArbAmountRaw)
			const deadline = Date.now() + ARB_POLL_DEADLINE_MS
			let landed = false

			while (Date.now() < deadline && !cancelled) {
				try {
					const current = await readArbBalance(eoa)
					if (current >= baseline + expected) {
						landed = true
						hlLog('[hl-bridge-watcher] arbitrum credit landed', {
							balanceRaw: current.toString(),
							elapsedSec: ((Date.now() - state.startedAt) / 1000).toFixed(1),
						})
						break
					}
				} catch (e) {
					console.warn('[hl-bridge-watcher] arbitrum read failed (RPC hiccup):', e)
				}
				refreshWatcherLock(tabId)
				await new Promise(r => setTimeout(r, ARB_POLL_INTERVAL_MS))
			}

			if (cancelled) return

			if (!landed) {
				// Surface the timeout so the user sees they need to act —
				// the toast.loading from useInternalTransfer would otherwise
				// hang forever. Leave the active-bridge state in place so
				// the stranded-recovery banner picks it up.
				console.warn('[hl-bridge-watcher] arbitrum credit timeout, deferring to banner')
				toast.error(t('withdrawTimeout'), { id: 'hl-bridge-watcher', duration: 10_000 })
				return
			}

			// Verify we still own the lock — if the stranded-recovery banner
			// in this tab or another fired in the gap, it cleared the
			// active-bridge state + lock and is running its own userOp.
			// Bailing here avoids draining the EOA a second time.
			if (!isWatcherLockOwned(tabId)) {
				console.warn('[hl-bridge-watcher] lock lost between poll and quote — deferring')
				return
			}

			toast.loading(t('bridging', { destination: state.destination }), {
				id: 'hl-bridge-watcher',
				description: t('bridgingDesc'),
			})

			refreshWatcherLock(tabId)
			const currentArbBalance = await readArbBalance(eoa)
			// Cap bridge amount at the expected withdraw amount — pre-existing
			// USDC on Arbitrum must NOT be swept to the destination chain.
			// Without this, a user with $50 pre-existing + $100 from withdraw
			// would bridge $150 instead of $100, losing $50.
			const bridgeAmount = currentArbBalance < expected ? currentArbBalance : expected
			const freshQuote = await fetchRelayQuoteWithRetry({
				user: eoa,
				originChainId: CHAIN_CONFIG.arbitrum.id,
				originCurrency: ARB_USDC,
				destinationChainId: state.destChainId,
				destinationCurrency: state.destCurrency,
				amount: bridgeAmount.toString(),
				recipient: state.destRecipient,
				tradeType: 'EXACT_INPUT',
			})
			hlLog('[hl-bridge-watcher] fresh quote', {
				arbBalance: (Number(currentArbBalance) / 1e6).toFixed(2),
				bridgeAmount: (Number(bridgeAmount) / 1e6).toFixed(2),
				steps: freshQuote.steps.length,
			})

			const ops = extractRelayOpsForChain(freshQuote.steps, CHAIN_CONFIG.arbitrum.id)
			if (ops.length === 0) {
				throw new Error('Relay quote produced no Arbitrum calls')
			}

			refreshWatcherLock(tabId)
			// Final lock check immediately before the userOp goes out — the
			// quote fetch took up to several seconds; a banner click in
			// that gap would have cleared our claim.
			if (!isWatcherLockOwned(tabId)) {
				console.warn('[hl-bridge-watcher] lock lost between quote and send — deferring')
				return
			}
			const accountForCalls = accountRef.current
			if (!accountForCalls) throw new Error('Signer unavailable for sponsored userOp')

			let hash: string
			try {
				hash = await sendSponsoredCalls({
					chain: 'arbitrum',
					account: accountForCalls,
					calls: ops,
				})
			} catch (sendErr) {
				// sendSponsoredCalls may throw on receipt-poll timeout even
				// when the userOp landed. Verify by re-reading Arbitrum
				// balance: if it dropped, the call succeeded.
				const postBalance = await readArbBalance(eoa).catch(() => currentArbBalance)
				if (postBalance < currentArbBalance / 2n) {
					console.warn('[hl-bridge-watcher] send threw but balance dropped — assuming success', {
						pre: currentArbBalance.toString(),
						post: postBalance.toString(),
					})
					toast.success(
						t('bridgeComplete', { amount: state.amountFormatted, destination: state.destination }),
						{ id: 'hl-bridge-watcher', duration: 6_000 }
					)
					clearActiveBridge()
					try {
						localStorage.removeItem('hl-withdraw-in-progress')
						localStorage.removeItem('hl-expected-withdraw-arb-raw')
					} catch {}
					return
				}
				throw sendErr
			}
			hlLog('[hl-bridge-watcher] relay leg landed', { hash })

			toast.success(
				t('bridgeComplete', { amount: state.amountFormatted, destination: state.destination }),
				{ id: 'hl-bridge-watcher', duration: 6_000 }
			)
			clearActiveBridge()
			try {
				localStorage.removeItem('hl-withdraw-in-progress')
				localStorage.removeItem('hl-expected-withdraw-arb-raw')
			} catch {}
		}

		async function tick() {
			if (cancelled || inFlightRef.current) return
			const state = readActiveBridge()
			if (!state) return

			if (Date.now() - state.startedAt > ACTIVE_BRIDGE_STALE_MS) {
				console.warn('[hl-bridge-watcher] entry > 30min, deferring to stranded banner')
				clearActiveBridge()
				return
			}

			if (!tryClaimWatcherLock(tabId)) {
				return
			}

			inFlightRef.current = true
			try {
				await runBridge(state)
			} catch (e) {
				console.error('[hl-bridge-watcher] failed:', e)
				captureApiError(e, {
					endpoint: 'hl_bridge_watcher',
					tags: { category: 'hyperliquid-bridge' },
					extra: {
						destination: state.destination,
						destChainId: state.destChainId,
						elapsedMs: Date.now() - state.startedAt,
					},
				})
				toast.error(t('bridgeFailed'), { id: 'hl-bridge-watcher', duration: 10_000 })
			} finally {
				inFlightRef.current = false
				releaseWatcherLock(tabId)
			}
		}

		tick().catch(() => {})
		const id = setInterval(() => {
			tick().catch(() => {})
		}, TICK_INTERVAL_MS)
		return () => {
			cancelled = true
			clearInterval(id)
			inFlightRef.current = false
			releaseWatcherLock(tabId)
		}
	}, [eoaForEffect, accountReady, t])
}
